import { IS_LOCAL_DEVELOPMENT } from "../const/environment";
import {
  LOCAL_LOG_FLUSH_MS,
  LOCAL_LOG_LEVELS,
  LOCAL_LOG_LOOPBACK_HOSTS,
  LOCAL_LOG_MAX_BATCH_BYTES,
  LOCAL_LOG_MAX_BATCH_ENTRIES,
  LOCAL_LOG_MAX_QUEUE,
  LOCAL_LOG_RETRY_MS,
  LOCAL_LOG_TIMEOUT_MS,
} from "../const/local-logs";
import type { LocalLogEntry, LocalLogLevel, LocalLogWindow } from "../types/local-logs";
import { browserSessionId, getFrontendErrorFields } from "../utils/frontend-logs";
import { serializeLocalLog } from "../utils/local-logs";

function installLocalLogCapture(): void {
  if (!IS_LOCAL_DEVELOPMENT || !LOCAL_LOG_LOOPBACK_HOSTS.has(window.location.hostname)) return;
  const logWindow = window as LocalLogWindow;
  const sessionId =
    process.env.REACT_APP_LOCAL_LOG_SESSION_ID ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  if (logWindow.__opslinkLocalLogCapture?.sessionId === sessionId) return;
  let endpoint: string;
  try {
    const api = new URL(process.env.REACT_APP_API_URL ?? "");
    if (!LOCAL_LOG_LOOPBACK_HOSTS.has(api.hostname) || !["http:", "https:"].includes(api.protocol)) return;
    endpoint = `${api.href.replace(/\/$/, "")}/local-logs/frontend`;
  } catch {
    return;
  }
  logWindow.__opslinkLocalLogCapture?.dispose();
  const originalWarn = console.warn.bind(console);
  const originalInfo = console.info.bind(console);
  let queue: LocalLogEntry[] = [];
  let timer: ReturnType<typeof setTimeout> | undefined;
  let inFlight = false;
  let initialized = false;
  let stopped = false;
  let warned = false;

  const schedule = (delay = LOCAL_LOG_FLUSH_MS) => {
    if (!timer && !stopped && !inFlight)
      timer = setTimeout(() => {
        timer = undefined;
        void flush();
      }, delay);
  };

  const capture = (level: LocalLogLevel, args: unknown[]) => {
    if (stopped) return;
    queue.push({ timestamp: new Date().toISOString(), level, message: serializeLocalLog(args) });
    if (queue.length > LOCAL_LOG_MAX_QUEUE) queue.splice(0, queue.length - LOCAL_LOG_MAX_QUEUE);
    schedule();
  };

  const flush = async () => {
    if (inFlight || stopped || (initialized && queue.length === 0)) return;
    clearTimeout(timer);
    timer = undefined;
    inFlight = true;
    const batch: LocalLogEntry[] = [];
    // A small payload also permits keepalive delivery while the browser is leaving the page.
    for (const entry of queue.slice(0, LOCAL_LOG_MAX_BATCH_ENTRIES)) {
      if (
        new TextEncoder().encode(JSON.stringify({ sessionId, entries: [...batch, entry] })).length >
        LOCAL_LOG_MAX_BATCH_BYTES
      )
        break;
      batch.push(entry);
    }
    queue.splice(0, batch.length);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), LOCAL_LOG_TIMEOUT_MS);
    let retryDelay = LOCAL_LOG_FLUSH_MS;
    let failure = "network";
    try {
      // Bypass the authenticated Axios service: no credentials, auth dependency, or recursive API-error logs.
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId, entries: batch }),
        credentials: "omit",
        keepalive: true,
        signal: controller.signal,
      });
      if ([403, 404, 409].includes(response.status)) {
        stopped = true;
        queue = [];
        originalWarn("FE logs - stopped (" + response.status + ")");
        return;
      }
      if (!response.ok) {
        failure = "HTTP " + response.status;
        throw new Error("Local log intake unavailable");
      }
      if (!initialized || warned) originalInfo("FE logs - connected");
      initialized = true;
      warned = false;
      retryDelay = queue.length > 0 ? 0 : LOCAL_LOG_FLUSH_MS;
    } catch (error) {
      if (stopped) return;
      queue = [...batch, ...queue].slice(-LOCAL_LOG_MAX_QUEUE);
      retryDelay = LOCAL_LOG_RETRY_MS;
      if (!warned) {
        warned = true;
        originalWarn(
          "FE logs - retrying (" + (error instanceof Error && error.name === "AbortError" ? "timeout" : failure) + ")",
        );
      }
    } finally {
      clearTimeout(timeout);
      inFlight = false;
      if (!stopped && (!initialized || queue.length > 0)) schedule(retryDelay);
    }
  };

  const restoreConsole: Array<() => void> = [];
  for (const level of LOCAL_LOG_LEVELS) {
    const original = console[level].bind(console);
    const wrapped = (...args: unknown[]) => {
      original(...args);
      capture(level, args);
    };
    console[level] = wrapped;
    restoreConsole.push(() => {
      if (console[level] === wrapped) console[level] = original;
    });
  }
  const onError = (event: ErrorEvent) =>
    capture("error", [
      "main - SUB-E error",
      { browserSessionId, ...getFrontendErrorFields(event.error), line: event.lineno },
    ]);
  const onRejection = (event: PromiseRejectionEvent) => {
    const fields = getFrontendErrorFields(event.reason);
    if (fields.code === "ERR_CANCELED" || fields.errorType === "AbortError" || fields.errorType === "CanceledError")
      return;
    capture("error", ["main - SUB-E unhandledrejection", { browserSessionId, ...fields }]);
  };
  const onPageHide = () => {
    void flush();
  };
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  window.addEventListener("pagehide", onPageHide);
  logWindow.__opslinkLocalLogCapture = {
    sessionId,
    dispose: () => {
      stopped = true;
      queue = [];
      clearTimeout(timer);
      restoreConsole.forEach((restore) => restore());
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      window.removeEventListener("pagehide", onPageHide);
    },
  };
  capture("info", ["FE logs - started", { browserSessionId }]);
  void flush();
}

installLocalLogCapture();
